import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import {
  getOperationsTatDashboard,
  OperationsTatDashboardRow,
} from '../../api/members';
import Button from '../../components/BaseComponents/Button';
import Loader from '../../components/BaseComponents/Loader';
import TableHeader from '../../components/BaseComponents/TableHeader';
import { formatTatDuration, formatUKDate } from '../../utils';

const PAGE_SIZE = 10;
const DATETIME_FORMAT = 'dd/MM/yyyy HH:mm';
const SEARCH_DEBOUNCE_MS = 400;

type HeaderColumn = {
  title: string;
  key: string;
  minWidth: string;
  width?: string;
  flex: number;
  cellClassName?: string;
  render: (row: OperationsTatDashboardRow) => React.ReactNode;
};

const cellStyle = (column: HeaderColumn): React.CSSProperties => ({
  minWidth: column.minWidth,
  width: column.width,
  flex: column.flex === 0 ? '0 0 auto' : `1 0 ${column.minWidth || 'auto'}`,
  flexShrink: 0,
});

const formatDashboardDate = (value: string | null) =>
  formatUKDate(value, DATETIME_FORMAT);

const TABLE_COLUMNS: HeaderColumn[] = [
  {
    title: 'Member name',
    key: 'memberName',
    minWidth: '140px',
    flex: 1,
    cellClassName: 'member-name',
    render: (row) => row.member_name || '-',
  },
  {
    title: 'Subscription created',
    key: 'subscriptionCreated',
    minWidth: '160px',
    width: '160px',
    flex: 0,
    cellClassName: 'milestone-date',
    render: (row) => formatDashboardDate(row.subscription_created_at),
  },
  {
    title: 'Booking appointment',
    key: 'bookingAppointment',
    minWidth: '160px',
    width: '160px',
    flex: 0,
    cellClassName: 'milestone-date',
    render: (row) => formatDashboardDate(row.booking_appointment_date),
  },
  {
    title: 'HL7 received',
    key: 'hl7Received',
    minWidth: '160px',
    width: '160px',
    flex: 0,
    cellClassName: 'milestone-date',
    render: (row) => formatDashboardDate(row.hl7_received_at),
  },
  {
    title: 'GP review',
    key: 'gpReview',
    minWidth: '160px',
    width: '160px',
    flex: 0,
    cellClassName: 'milestone-date',
    render: (row) => formatDashboardDate(row.gp_review_date),
  },
  {
    title: 'Nutritionist appt',
    key: 'nutritionistAppt',
    minWidth: '160px',
    width: '160px',
    flex: 0,
    cellClassName: 'milestone-date',
    render: (row) => formatDashboardDate(row.nutritionist_appointment_date),
  },
  {
    title: 'TAT booking',
    key: 'tatBooking',
    minWidth: '120px',
    width: '120px',
    flex: 0,
    cellClassName: 'tat-value',
    render: (row) => formatTatDuration(row.tat_booking_minutes),
  },
  {
    title: 'TAT receiving results',
    key: 'tatReceiving',
    minWidth: '160px',
    width: '160px',
    flex: 0,
    cellClassName: 'tat-value',
    render: (row) => formatTatDuration(row.tat_receiving_results_minutes),
  },
  {
    title: 'TAT laboratory',
    key: 'tatLab',
    minWidth: '130px',
    width: '130px',
    flex: 0,
    cellClassName: 'tat-value',
    render: (row) => formatTatDuration(row.tat_laboratory_minutes),
  },
  {
    title: 'TAT results published',
    key: 'tatPublished',
    minWidth: '160px',
    width: '160px',
    flex: 0,
    cellClassName: 'tat-value',
    render: (row) => formatTatDuration(row.tat_results_published_minutes),
  },
  {
    title: 'TAT nutritionist',
    key: 'tatNutritionist',
    minWidth: '140px',
    width: '140px',
    flex: 0,
    cellClassName: 'tat-value',
    render: (row) => formatTatDuration(row.tat_nutritionist_minutes),
  },
  {
    title: 'TAT overall',
    key: 'tatOverall',
    minWidth: '120px',
    width: '120px',
    flex: 0,
    cellClassName: 'tat-value',
    render: (row) => formatTatDuration(row.tat_overall_minutes),
  },
];

const OperationsTatDashboardTab: React.FC = () => {
  const [rows, setRows] = useState<OperationsTatDashboardRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [memberNameInput, setMemberNameInput] = useState('');
  const [memberNameFilter, setMemberNameFilter] = useState('');
  const [offset, setOffset] = useState(0);

  const headerColumns = useMemo(
    () =>
      TABLE_COLUMNS.map(({ title, key, minWidth, width, flex }) => ({
        title,
        key,
        minWidth,
        width,
        flex,
      })),
    []
  );

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setMemberNameFilter(memberNameInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(handle);
  }, [memberNameInput]);

  useEffect(() => {
    setOffset(0);
  }, [memberNameFilter]);

  const applyMemberSearch = () => {
    setMemberNameFilter(memberNameInput.trim());
    setOffset(0);
  };

  const handleSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyMemberSearch();
    }
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getOperationsTatDashboard(
        PAGE_SIZE,
        offset,
        memberNameFilter || undefined
      );
      setRows(response.rows ?? []);
      setTotalCount(response.total_count ?? 0);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to load operations TAT dashboard';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [offset, memberNameFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const maxPage = Math.max(0, Math.ceil(totalCount / PAGE_SIZE) - 1);
  const currentPage = Math.floor(offset / PAGE_SIZE);

  return (
    <div className='clinical-dashboard-tab-panel'>
      <div className='search-row-container'>
        <div className='search-and-filter-container'>
          <div className='clinical-dashboard-filters'>
            <div className='search-input-container'>
              <input
                type='text'
                className='search-input'
                value={memberNameInput}
                onChange={(event) => setMemberNameInput(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder='Search by member name'
              />
            </div>
          </div>
          <div className='count-container' title='Total members shown in this dashboard'>
            {totalCount} {totalCount === 1 ? 'total member' : 'total members'}
          </div>
        </div>
      </div>

      <div className='clinical-dashboard-table-section'>
        <div className='scroll-container'>
          <div className='table-container clinical-dashboard-table operations-tat-dashboard-table mt-0'>
            <TableHeader headerColumns={headerColumns} gap='24px' />
            {loading ? (
              <div className='clinical-dashboard-loading'>
                <Loader height='48px' />
              </div>
            ) : rows.length === 0 ? (
              <div className='empty-members'>No rows found.</div>
            ) : (
              <div className='table-body'>
                {rows.map((row) => (
                  <div key={row.member_id} className='table-row'>
                    {TABLE_COLUMNS.map((column) => (
                      <div
                        key={column.key}
                        className={column.cellClassName}
                        style={cellStyle(column)}
                      >
                        {column.render(row)}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='clinical-dashboard-pagination'>
        <Button
          text='Previous'
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          isDisabled={currentPage === 0 || loading}
          width='90px'
          className='btn-sm'
        />
        <span className='pagination-label'>
          Page {currentPage + 1} of {maxPage + 1}
        </span>
        <Button
          text='Next'
          onClick={() => setOffset(Math.min(maxPage * PAGE_SIZE, offset + PAGE_SIZE))}
          isDisabled={currentPage >= maxPage || loading}
          width='90px'
          className='btn-sm'
        />
      </div>
    </div>
  );
};

export default OperationsTatDashboardTab;
